import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Trophy, BookOpen, Brain, LogOut, TrendingUp,
  Award, Calendar, Search, Radio, RefreshCw, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { adminGetLearners, adminGetAnalytics, adminGetLearnerDetail, adminLogout, getAdminToken, adminReissueCertificate } from '@/utils/api';
import { getBadgeInfo } from '@/data/badgeCatalog';
import { LANGUAGES, badgeIdFor, getLangInfo } from '@/data/languages';
import { getLessonById } from '@/data/lessonsData';
import { getConceptById } from '@/data/conceptsData';

const CERTIFIABLE_BASES = ['java-master', 'dsa-champion', 'full-stack-learner'];

const isLive = (lastActive) => {
  if (!lastActive) return false;
  const diffMs = Date.now() - new Date(lastActive).getTime();
  return diffMs < 60_000; // last 60s
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-[#0F0F12] border border-white/10 rounded-xl p-6"
  >
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: color + '20', border: `1px solid ${color}40` }}>
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
      <div>
        <div className="text-[#71717A] text-xs font-code mb-1">{label}</div>
        <div className="text-white text-3xl font-heading font-bold">{value}</div>
      </div>
    </div>
  </motion.div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [learners, setLearners] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [reissueLang, setReissueLang] = useState('java');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [reissuingKey, setReissuingKey] = useState(null);
  const selectedIdRef = useRef(null);

  const loadAll = async (silent = false) => {
    if (!silent) setRefreshing(true);
    const [l, a] = await Promise.all([adminGetLearners(), adminGetAnalytics()]);
    setLearners(l);
    setAnalytics(a);
    // Refresh open detail if any
    if (selectedIdRef.current) {
      const d = await adminGetLearnerDetail(selectedIdRef.current);
      setDetail(d);
    }
    setLastRefresh(new Date());
    if (!silent) setRefreshing(false);
  };

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin');
      return;
    }
    const init = async () => {
      await loadAll(true);
      setLoading(false);
    };
    init();
    // Poll every 5s
    const interval = setInterval(() => {
      loadAll(true);
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const openDetail = async (learner) => {
    setSelectedLearner(learner);
    selectedIdRef.current = learner.id;
    const d = await adminGetLearnerDetail(learner.id);
    setDetail(d);
  };

  const closeDetail = () => {
    setSelectedLearner(null);
    setDetail(null);
    selectedIdRef.current = null;
  };

  const handleReissue = async (learnerId, badgeId, badgeName) => {
    const key = `${learnerId}-${badgeId}`;
    setReissuingKey(key);
    const res = await adminReissueCertificate(learnerId, badgeId);
    setReissuingKey(null);
    if (res) {
      toast.success(res.reissued ? `Re-issued ${badgeName}` : `Issued ${badgeName}`, {
        description: 'Learner can now download the updated certificate.',
      });
      // Refresh detail
      const d = await adminGetLearnerDetail(learnerId);
      setDetail(d);
      loadAll(true);
    } else {
      toast.error('Failed to re-issue certificate');
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate('/admin');
  };

  const filteredLearners = learners.filter(l =>
    l.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Admin Header */}
      <header className="fixed top-0 w-full h-16 backdrop-blur-xl bg-[#050505]/80 border-b border-[#FFB800]/20 z-50 flex items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FFB800] to-[#FF8800] flex items-center justify-center">
            <Trophy className="w-4 h-4 text-black" />
          </div>
          <div>
            <div className="text-white font-heading font-semibold">Admin Portal</div>
            <div className="text-[#71717A] text-xs font-code">DSA With Kapil</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 text-xs font-code text-[#71717A]">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span>LIVE · Auto-refresh 5s</span>
            {lastRefresh && (
              <span className="text-[#52525B]">
                · {lastRefresh.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
          </div>
          <Button
            onClick={() => loadAll(false)}
            variant="outline"
            size="sm"
            data-testid="admin-refresh-button"
            className="border-white/10 text-white hover:bg-white/5"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleLogout}
            variant="outline"
            data-testid="admin-logout-button"
            className="border-white/10 text-white hover:bg-white/5"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <main data-testid="admin-dashboard-container" className="pt-24 pb-12 max-w-7xl mx-auto px-6">
        {loading ? (
          <div className="text-center py-20 text-[#71717A] font-body">Loading admin data...</div>
        ) : (
          <>
            {/* Analytics Cards */}
            {analytics && (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <StatCard label="TOTAL LEARNERS" value={analytics.total_learners} icon={Users} color="#FFB800" />
                <StatCard label="QUIZZES DONE" value={analytics.total_quizzes_completed} icon={Brain} color="#007AFF" />
                <StatCard label="LESSONS DONE" value={analytics.total_lessons_completed} icon={BookOpen} color="#22C55E" />
                <StatCard label="BADGES EARNED" value={analytics.total_badges_earned} icon={Award} color="#EC4899" />
                <StatCard label="ASSESSMENTS" value={analytics.total_assessments_completed ?? 0} icon={Award} color="#F97316" />
                <StatCard label="ACTIVITIES" value={analytics.total_activities} icon={TrendingUp} color="#A855F7" />
              </div>
            )}

            {/* Top Badges & Lessons */}
            {analytics && (analytics.top_badges.length > 0 || analytics.top_lessons.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-6">
                  <h3 className="text-white font-heading font-semibold mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#FFB800]" />
                    Most Earned Badges
                  </h3>
                  <div className="space-y-2">
                    {analytics.top_badges.length === 0 ? (
                      <div className="text-[#71717A] text-sm font-body italic">No badges earned yet</div>
                    ) : analytics.top_badges.slice(0, 5).map((b, i) => (
                      <div key={i} className="flex items-center justify-between bg-[#1A1A20] rounded px-3 py-2">
                        <span className="text-white text-sm font-body">{b.name}</span>
                        <span className="text-[#FFB800] font-code font-bold text-sm">{b.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-6">
                  <h3 className="text-white font-heading font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#007AFF]" />
                    Popular Lessons
                  </h3>
                  <div className="space-y-2">
                    {analytics.top_lessons.length === 0 ? (
                      <div className="text-[#71717A] text-sm font-body italic">No lessons completed yet</div>
                    ) : analytics.top_lessons.slice(0, 5).map((l, i) => {
                      const lesson = getLessonById(l.lesson_id);
                      return (
                        <div key={i} className="flex items-center justify-between bg-[#1A1A20] rounded px-3 py-2">
                          <span className="text-white text-sm font-body">{lesson?.title || l.lesson_id}</span>
                          <span className="text-[#007AFF] font-code font-bold text-sm">{l.count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Learners Table */}
            <div className="bg-[#0F0F12] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10 flex items-center justify-between gap-4">
                <h2 className="text-white font-heading text-xl font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#FFB800]" />
                  All Learners ({learners.length})
                </h2>
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search learners..."
                    className="pl-9 bg-[#1A1A20] border-white/20 text-white font-code"
                    data-testid="admin-search-input"
                  />
                </div>
              </div>

              {filteredLearners.length === 0 ? (
                <div className="p-12 text-center text-[#71717A] font-body">No learners yet</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10 text-[#71717A] text-xs font-code uppercase">
                        <th className="text-left p-4">Name</th>
                        <th className="text-center p-4">Quizzes</th>
                        <th className="text-center p-4">Lessons</th>
                        <th className="text-center p-4">Badges</th>
                        <th className="text-center p-4">Final Assessment</th>
                        <th className="text-left p-4">Last Active</th>
                        <th className="p-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLearners.map((l) => (
                        <tr
                          key={l.id}
                          data-testid={`learner-row-${l.id}`}
                          className="border-b border-white/5 hover:bg-white/5 transition-colors"
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="relative w-8 h-8 rounded-full bg-[#FFB800]/20 border border-[#FFB800]/40 flex items-center justify-center text-[#FFB800] font-code font-bold text-sm">
                                {l.name[0]?.toUpperCase()}
                                {isLive(l.last_active) && (
                                  <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500 border border-[#050505]"></span>
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-col">
                                <span className="text-white font-body">{l.name}</span>
                                {isLive(l.last_active) && (
                                  <span className="text-[10px] font-code text-green-400 uppercase tracking-wider flex items-center gap-1">
                                    <Radio className="w-2.5 h-2.5" /> Live now
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="text-center p-4">
                            <Badge className="bg-[#007AFF]/10 text-[#007AFF] border-[#007AFF]/20 border font-code">
                              {l.quiz_count}/10
                            </Badge>
                          </td>
                          <td className="text-center p-4">
                            <Badge className="bg-green-500/10 text-green-400 border-green-500/20 border font-code">
                              {l.lesson_count}/30
                            </Badge>
                          </td>
                          <td className="text-center p-4">
                            <Badge className="bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/20 border font-code">
                              {l.badge_count}/16
                            </Badge>
                          </td>
                          <td className="text-center p-4" data-testid={`learner-assessment-${l.id}`}>
                            {l.best_assessment ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <Badge className="bg-[#EC4899]/10 text-[#EC4899] border-[#EC4899]/20 border font-code">
                                  {l.best_assessment.score}/{l.best_assessment.total}
                                </Badge>
                                {l.best_assessment.time_taken_sec != null && (
                                  <span className="text-[10px] text-[#71717A] font-code">
                                    {Math.floor(l.best_assessment.time_taken_sec / 60)}m {l.best_assessment.time_taken_sec % 60}s
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-[#52525B] text-xs font-code">—</span>
                            )}
                          </td>
                          <td className="p-4 text-[#71717A] text-xs font-code">
                            {new Date(l.last_active).toLocaleString('en-US', {
                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </td>
                          <td className="p-4 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-white/10 text-white hover:bg-white/5"
                              onClick={() => openDetail(l)}
                              data-testid={`view-learner-${l.id}`}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Learner Detail Modal */}
        {selectedLearner && detail && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeDetail}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0F0F12] border border-white/10 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#FFB800]/20 border border-[#FFB800]/40 flex items-center justify-center text-[#FFB800] font-code font-bold text-lg">
                    {selectedLearner.name[0]?.toUpperCase()}
                  </div>
                  <div>
                    <h2 className="text-white font-heading text-xl font-bold">{selectedLearner.name}</h2>
                    <div className="text-[#71717A] text-xs font-code flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Joined {new Date(selectedLearner.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <button onClick={closeDetail} className="text-[#71717A] hover:text-white text-2xl">×</button>
              </div>

              {/* Badges */}
              <div className="mb-6">
                <h3 className="text-white font-heading font-semibold mb-3">
                  Badges Earned ({detail.badges.length})
                </h3>
                {detail.badges.length === 0 ? (
                  <div className="text-[#71717A] text-sm font-body italic">No badges yet</div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {detail.badges.map(b => {
                      const info = getBadgeInfo(b.badge_id);
                      return (
                        <div key={b.id} className="bg-[#1A1A20] border rounded-lg px-3 py-2 flex items-center gap-2" style={{ borderColor: info.color + '40' }}>
                          <span className="text-xl">{info.icon}</span>
                          <div>
                            <div className="text-white text-xs font-code font-bold">{info.name}</div>
                            <div className="text-[#71717A] text-[10px] font-code">{info.tier}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Certificate Re-Issue */}
              <div className="mb-6 bg-[#1A1A20] border border-[#FFB800]/20 rounded-lg p-4">
                <h3 className="text-white font-heading font-semibold mb-1 flex items-center gap-2">
                  <Send className="w-4 h-4 text-[#FFB800]" />
                  Re-Issue Certificates
                </h3>
                <p className="text-[#71717A] text-xs font-code mb-3">
                  Force-award or refresh a certifiable badge for this learner.
                </p>
                <div className="flex gap-1 mb-3" data-testid="reissue-lang-group">
                  {LANGUAGES.map(l => (
                    <button
                      key={l.id}
                      data-testid={`reissue-lang-${l.id}`}
                      onClick={() => setReissueLang(l.id)}
                      className={`px-3 py-1 rounded-full text-xs font-code font-bold transition-colors ${reissueLang === l.id ? 'text-black' : 'text-[#A1A1AA] bg-white/5 hover:text-white'}`}
                      style={reissueLang === l.id ? { background: l.color } : undefined}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {CERTIFIABLE_BASES.map(base => ({ id: badgeIdFor(base, reissueLang), name: getBadgeInfo(badgeIdFor(base, reissueLang)).name })).map(b => {
                    const owned = detail.badges.some(x => x.badge_id === b.id);
                    const key = `${selectedLearner.id}-${b.id}`;
                    const info = getBadgeInfo(b.id);
                    return (
                      <Button
                        key={b.id}
                        data-testid={`reissue-btn-${b.id}`}
                        onClick={() => handleReissue(selectedLearner.id, b.id, b.name)}
                        disabled={reissuingKey === key}
                        variant="outline"
                        size="sm"
                        className="border-white/10 hover:bg-white/5 text-left justify-start h-auto py-2"
                        style={owned ? { borderColor: info.color + '60' } : {}}
                      >
                        <span className="text-lg mr-2">{info.icon}</span>
                        <div className="flex flex-col items-start">
                          <span className="text-white text-xs font-code font-bold">
                            {reissuingKey === key ? 'Working...' : (owned ? 'Re-Issue' : 'Issue')}
                          </span>
                          <span className="text-[#71717A] text-[10px]">{b.name}</span>
                        </div>
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Activities */}
              <div>
                <h3 className="text-white font-heading font-semibold mb-3">
                  Recent Activities ({detail.activities.length})
                </h3>
                {detail.activities.length === 0 ? (
                  <div className="text-[#71717A] text-sm font-body italic">No activity yet</div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {[...detail.activities].reverse().map(a => {
                      let label = a.reference_id;
                      let icon = '⚡';
                      let typeLabel = 'Lesson';
                      if (a.type === 'quiz_completed') {
                        icon = '🧠';
                        typeLabel = 'Quiz';
                        label = getConceptById(a.reference_id)?.title || a.reference_id;
                      } else if (a.type === 'lesson_completed') {
                        icon = '⚡';
                        typeLabel = 'Lesson';
                        label = getLessonById(a.reference_id)?.title || a.reference_id;
                      } else if (a.type === 'challenge_completed') {
                        icon = '🎯';
                        typeLabel = 'Challenge';
                        label = a.reference_id.charAt(0).toUpperCase() + a.reference_id.slice(1).replace(/-/g, ' ');
                      } else if (a.type === 'final_assessment_completed') {
                        icon = '🏆';
                        typeLabel = 'Final Assessment';
                        label = a.time_taken_sec != null
                          ? `${a.reference_id} · ${Math.floor(a.time_taken_sec / 60)}m ${a.time_taken_sec % 60}s`
                          : a.reference_id;
                      }
                      return (
                        <div key={a.id} className="bg-[#1A1A20] border border-white/10 rounded px-3 py-2 flex items-center justify-between">
                          <div>
                            <div className="text-white text-sm font-body">
                              {icon} {typeLabel} · {label}
                              <span className="ml-2 text-[10px] font-code px-1.5 py-0.5 rounded bg-white/5" style={{ color: getLangInfo(a.language || 'java').color }}>{getLangInfo(a.language || 'java').label}</span>
                            </div>
                            <div className="text-[#71717A] text-xs font-code">
                              {new Date(a.created_at).toLocaleString()}
                            </div>
                          </div>
                          {a.score !== null && a.score !== undefined && (
                            <Badge className={`border font-code ${
                              a.score === a.total 
                                ? 'bg-green-500/10 text-green-400 border-green-500/20' 
                                : 'bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/20'
                            }`}>
                              {a.score}/{a.total}
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
